import { get, post, patch } from './axios.config';
import tenantsService from './tenants.service';
import localStorageService from '../storage/localStorage.service';

/**
 * Trata erros da API e faz retry se necessário
 */
const handleApiCall = async (apiCall, retryOnAuth = true) => {
  try {
    return await apiCall();
  } catch (error) {
    // Se erro 401 e não é admin, tenta renovar token
    if (error.response?.status === 401 && retryOnAuth) {
      const userData = localStorageService.getUserData();
      const isAdmin = userData?.type === 'admin' || userData?.role === 'admin';
      
      if (!isAdmin && userData?.tenantId) {
        console.log('🔄 Token expirado, renovando...');
        
        try {
          // Gera novo token
          const tokenData = await tenantsService.generateToken(userData.tenantId);
          localStorageService.setAuthToken(tokenData.token);
          
          console.log('✅ Token renovado, tentando novamente...');
          
          // Retry a chamada
          return await apiCall();
        } catch (retryError) {
          console.error('❌ Erro ao renovar token:', retryError);
          throw new Error('Sessão expirada. Faça login novamente.');
        }
      }
    }
    
    // Se não conseguiu tratar, lança o erro
    throw handleError(error);
  }
};

/**
 * Formata erros da API
 */
const handleError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    
    if (status === 401) {
      return new Error('Não autorizado. Faça login novamente.');
    }
    
    if (status === 403) {
      return new Error('Você não tem permissão para acessar este recurso.');
    }
    
    if (status === 404) {
      return new Error('Recurso não encontrado.');
    }
    
    return new Error(data?.error || data?.message || 'Erro ao processar requisição');
  }
  
  if (error.request) {
    return new Error('Sem resposta do servidor. Verifique sua conexão.');
  }
  
  return new Error(error.message || 'Erro desconhecido');
};

/**
 * Serviço de gerenciamento de conversas (Tenant)
 */
const conversationsService = {
  /**
   * Lista conversas de um tenant (uma página)
   */
  getConversations: async (tenantId, params = {}) => {
    return handleApiCall(async () => {
      const { status = 'open', limit = 50, cursor } = params;

      let url = `/api/tenant/${tenantId}/conversations?status=${status}&limit=${limit}`;
      if (cursor) url += `&cursor=${cursor}`;

      console.log('📡 Requisição:', url);
      const response = await get(url);
      console.log('✅ Resposta recebida');
      return response.data;
    });
  },

  /**
   * Busca conversas percorrendo a paginação por cursor.
   * Se fromDate/toDate forem fornecidos, filtra pelo intervalo usando lastInboundAt
   * (campo pelo qual a API ordena). Para de paginar quando os itens ficam mais antigos que fromDate.
   */
  getAllConversations: async (tenantId, params = {}) => {
    const { status = 'all', pageSize = 100, fromDate = null, toDate = null } = params;
    const all = [];
    const seenIds = new Set();
    let cursor = null;

    do {
      const data = await conversationsService.getConversations(tenantId, {
        status,
        limit: pageSize,
        cursor,
      });

      const items = data.items || data.conversations || [];
      cursor = data.nextCursor || null;

      for (const item of items) {
        // Usa lastInboundAt pois é o campo de ordenação da API
        const itemDate = new Date(item.lastInboundAt || item.createdAt);
        if (fromDate && itemDate < fromDate) continue;
        if (toDate && itemDate >= toDate) continue;
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          all.push(item);
        }
      }

      // Para de paginar quando algum item já está antes do fromDate
      // (a API ordena por lastInboundAt desc, então tudo depois só fica mais antigo)
      if (fromDate) {
        const hasOlderItem = items.some(
          (item) => new Date(item.lastInboundAt || item.createdAt) < fromDate
        );
        if (hasOlderItem) break;
      }

      console.log(`📦 ${items.length} conversas | total: ${all.length} | nextCursor: ${cursor}`);
    } while (cursor);

    return all;
  },

  /**
   * Busca mensagens de uma conversa
   */
  getMessages: async (tenantId, conversationId) => {
    return handleApiCall(async () => {
      const response = await get(`/api/tenant/${tenantId}/conversations/${conversationId}/messages`);
      return response.data;
    });
  },

  /**
   * Fecha uma conversa
   */
  closeConversation: async (tenantId, conversationId, data = {}) => {
    return handleApiCall(async () => {
      const response = await patch(
        `/api/tenant/${tenantId}/conversations/${conversationId}/close`,
        data
      );
      return response.data;
    });
  },

  /**
   * Pausa o bot para um contato (handoff)
   */
  pauseBot: async (waId, tenantId) => {
    return handleApiCall(async () => {
      const response = await post('/handoff/pause', {
        wa_id: waId,
        tenant_id: tenantId,
      });
      return response.data;
    }, false); // Handoff não precisa de retry com token
  },

  /**
   * Retoma o bot para um contato
   */
  resumeBot: async (waId, tenantId) => {
    return handleApiCall(async () => {
      const response = await post('/handoff/resume', {
        wa_id: waId,
        tenant_id: tenantId,
      });
      return response.data;
    }, false); // Handoff não precisa de retry com token
  },

  /**
   * Envia uma mensagem de texto para um contato
   * @param {string} tenantId
   * @param {{ to: string, text: string, type?: string, pauseBot?: boolean }} payload
   */
  sendMessage: async (tenantId, payload) => {
    return handleApiCall(async () => {
      const response = await post(`/api/tenant/${tenantId}/messages`, {
        type: 'text',
        pauseBot: true,
        ...payload,
      });
      return response.data; // { ok, conversationId, messageId, pauseBot, provider, ... }
    });
  },
};

export default conversationsService;